'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '@/lib/integrations/i18n'; // Initialize i18n

export type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  initializeFromProfile: (lang: Language) => void; // Silent initialization from profile
  isRTL: boolean;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>('en');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('language') as Language | null;
    const initialLang = (stored === 'ar' || stored === 'en') ? stored : 'en';

    setLanguageState(initialLang);
    i18n.changeLanguage(initialLang);

    // Update HTML attributes
    document.documentElement.lang = initialLang;
    document.documentElement.dir = initialLang === 'ar' ? 'rtl' : 'ltr';

    setIsLoading(false);
  }, [i18n]);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);

    // Update HTML attributes immediately
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';

    // Persist to Supabase (fire-and-forget, dialog will prompt restart anyway)
    try {
      await fetch('/api/profile/language', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: lang }),
      });
    } catch {
      // Silently ignore - dialog will prompt restart anyway
    }
  }, [i18n]);

  // Silent initialization from profile - doesn't trigger reload dialog or persist to DB
  const initializeFromProfile = useCallback((lang: Language) => {
    // Only sync if no localStorage preference exists (new device/browser)
    const stored = localStorage.getItem('language');
    if (!stored && lang !== language) {
      setLanguageState(lang);
      localStorage.setItem('language', lang);
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  }, [i18n, language]);

  const isRTL = language === 'ar';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, initializeFromProfile, isRTL, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
